import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { quizApi } from '../services/quizApi';
import '../styles/Quiz.css'; // Import the main quiz styles for shared components
import { useCallback } from 'react';

const QuizList = () => {
  const navigate = useNavigate();
  const [milestoneQuizzes, setMilestoneQuizzes] = useState([]);
  const [dailyQuizzes, setDailyQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userEmail, setUserEmail] = useState(localStorage.getItem('userEmail') || '');
  const [userVerified, setUserVerified] = useState(localStorage.getItem('userVerified') === 'true');
  const [userLastPrepLetterId, setUserLastPrepLetterId] = useState(0);
  
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [quizzesPerPage] = useState(6);
  
  // Sorting states
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  
  // Filtering and tabs
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'completed', 'uncompleted'
  const [filterOption, setFilterOption] = useState('all'); // 'all', 'completed', 'uncompleted'
  const [searchId, setSearchId] = useState(''); // Search by quiz/letter ID
  const [activeQuizType, setActiveQuizType] = useState('daily'); // 'daily', 'milestone'

  // Apply the quiz-body class for the background effect
  useEffect(() => {
    document.body.classList.add("quiz-body");
    return () => {
      document.body.classList.remove("quiz-body");
    };
  }, []);

  const fetchAllQuizzes = useCallback(async () => {
    if (!userEmail || !userVerified) {
      setLoading(false);
      navigate('/login?returnTo=/quizzes');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await quizApi.getAllQuizzes(userEmail);
      
      // Debug log to check the data structure
      setMilestoneQuizzes(response.milestone_quizzes || []);
      setDailyQuizzes(response.daily_quizzes || []);
      setUserLastPrepLetterId(response.user_last_prep_letter_id || 0);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load quizzes');
    } finally {
      setLoading(false);
    }
  }, [userEmail, userVerified, navigate]);

  // Check authentication status on component mount and when localStorage changes
  useEffect(() => {
    const storedEmail = localStorage.getItem('userEmail');
    const isVerified = localStorage.getItem('userVerified') === 'true';
    
    setUserEmail(storedEmail || '');
    setUserVerified(isVerified);
    
    if (storedEmail && isVerified) {
      fetchAllQuizzes();
    } else {
      navigate('/login?returnTo=/quizzes');
    }
  }, [fetchAllQuizzes, navigate]);
  
  // Sort quizzes by letter_id
  const sortQuizzesByLetterId = (quizzes, order) => {
    return [...quizzes].sort((a, b) => {
      const letterIdA = parseInt(a.letter_id) || 0;
      const letterIdB = parseInt(b.letter_id) || 0;
      return order === 'asc' ? letterIdA - letterIdB : letterIdB - letterIdA;
    });
  };
  
  // Filter quizzes based on completion status and search ID
  const filterQuizzes = (quizzes, filter, searchTerm = '') => {
    // Make a defensive copy of quizzes to ensure we have an array
    let safeQuizzes = Array.isArray(quizzes) ? [...quizzes] : [];
    
    // First filter by search ID if provided
    if (searchTerm.trim()) {
      const searchNumber = parseInt(searchTerm.trim());
      if (!isNaN(searchNumber)) {
        safeQuizzes = safeQuizzes.filter(quiz => {
          // For daily quizzes, search by letter_id
          if (quiz.letter_id) {
            return parseInt(quiz.letter_id) === searchNumber;
          }
          // For milestone quizzes, search by quiz_id
          if (quiz.quiz_id) {
            return parseInt(quiz.quiz_id) === searchNumber;
          }
          return false;
        });
      } else {
        // If search term is not a number, return empty array
        return [];
      }
    }
    
    // Then filter by completion status
    switch (filter) {
      case 'completed':
        return safeQuizzes.filter(quiz => Boolean(quiz.completed) === true);
      case 'uncompleted':
        return safeQuizzes.filter(quiz => Boolean(quiz.completed) !== true);
      case 'all':
      default:
        return safeQuizzes;
    }
  };
  
  // Handle page change for pagination
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  // Handle sort order change
  const handleSortOrderChange = () => {
    setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
  };
  
  // Handle filter change
  const handleFilterChange = (filter) => {
    setFilterOption(filter);
    setActiveTab(filter);
    setCurrentPage(1); // Reset to first page when changing filters
  };
  
  // Handle quiz type change
  const handleQuizTypeChange = (quizType) => {
    setActiveQuizType(quizType);
    setCurrentPage(1); // Reset to first page when changing quiz type
    // Reset filters when switching quiz types
    setFilterOption('all');
    setActiveTab('all');
    setSearchId('');
  };
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchId(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };
  
  // Clear search
  const handleClearSearch = () => {
    setSearchId('');
    setCurrentPage(1);
  };

  const handleStartQuiz = (quiz) => {
    // Navigate directly to quiz - popup will be handled on quiz page
    if (quiz.quiz_type === 'daily_letter' && quiz.letter_id) {
      navigate(`/quiz/daily/${quiz.letter_id}`);
    } else {
      navigate(`/quiz/${quiz.quiz_id}`);
    }
  };


  const getScoreBadgeClass = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'danger';
  };

  const getMilestoneBadgeClass = (milestoneDay) => {
    const colors = ['primary', 'info', 'success', 'warning', 'danger'];
    return colors[(milestoneDay / 7 - 1) % colors.length] || 'secondary';
  };

  // Get current quizzes based on active type
  const getCurrentQuizzes = () => {
    return activeQuizType === 'daily' ? dailyQuizzes : milestoneQuizzes;
  };

  // Find today's quiz based on user's last prep letter ID
  const getTodaysQuiz = () => {
    if (activeQuizType !== 'daily' || dailyQuizzes.length === 0) return null;
    
    if (activeQuizType !== 'daily' || dailyQuizzes.length === 0) return null;
    
    // First try exact match with user's last prep letter ID
    let todaysQuiz = null;
    if (userLastPrepLetterId > 0) {
      todaysQuiz = dailyQuizzes.find(quiz => quiz.letter_id === userLastPrepLetterId.toString());
    }
    
    // If no exact match found, find the highest available quiz that's <= user's progress
    if (!todaysQuiz && userLastPrepLetterId > 0) {
      const sortedQuizzes = [...dailyQuizzes]
        .filter(quiz => parseInt(quiz.letter_id) <= userLastPrepLetterId)
        .sort((a, b) => parseInt(b.letter_id) - parseInt(a.letter_id));
      todaysQuiz = sortedQuizzes[0];
    }
    
    // Final fallback: show the first available daily quiz
    if (!todaysQuiz && dailyQuizzes.length > 0) {
      const sortedQuizzes = [...dailyQuizzes].sort((a, b) => parseInt(a.letter_id) - parseInt(b.letter_id));
      todaysQuiz = sortedQuizzes[0];
    }
    
    return todaysQuiz;
  };

  // Apply sorting and filtering to current quizzes
  const getProcessedQuizzes = () => {
    const currentQuizzes = getCurrentQuizzes();
    const sortedQuizzes = activeQuizType === 'daily' 
      ? sortQuizzesByLetterId(currentQuizzes, sortOrder)
      : currentQuizzes; // Milestone quizzes don't need sorting by letter_id
    return filterQuizzes(sortedQuizzes, filterOption, searchId);
  };

  if (loading) {
    return (
      <div className="quiz-state-container">
        <div className="spinner-modern"></div>
        <p className="quiz-state-text">Loading available quizzes...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quiz-container-modern">
        <div className="quiz-state-container">
          <div className="alert-modern alert-danger-modern">
            <h4>Error</h4>
            <p>{error}</p>
            <button 
              className="btn-modern btn-secondary-modern mt-3" 
              onClick={fetchAllQuizzes}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-container-modern">
      {/* Header */}
      <div className="quiz-card mb-4">
        <div className="quiz-card-header d-flex justify-content-between align-items-center">
          <div>
            <h3 className="quiz-title-modern">Available Quizzes</h3>
            <p className="quiz-description-modern">Test your knowledge with milestone and daily quizzes</p>
          </div>
          <button 
            className="btn-modern btn-secondary-modern" 
            onClick={fetchAllQuizzes}
          >
            <i className="bi bi-arrow-clockwise"></i> Refresh
          </button>
        </div>
      </div>
      
      {/* Quiz Type Tabs */}
      <div className="quiz-tabs mb-4">
        <button 
          className={`quiz-tab ${activeQuizType === 'daily' ? 'active' : ''}`}
          onClick={() => handleQuizTypeChange('daily')}
        >
          <i className="bi bi-envelope-paper me-2"></i>Daily Quizzes
        </button>
        <button 
          className={`quiz-tab ${activeQuizType === 'milestone' ? 'active' : ''}`}
          onClick={() => handleQuizTypeChange('milestone')}
        >
          <i className="bi bi-trophy me-2"></i>Milestone Quizzes
        </button>
      </div>

      {/* No Quizzes Available */}
      {getCurrentQuizzes().length === 0 ? (
        <div className="quiz-card">
          <div className="quiz-card-body text-center py-5">
            <i className={`bi ${activeQuizType === 'daily' ? 'bi-envelope-x' : 'bi-trophy'} display-1 text-muted mb-3`}></i>
            <h4 className="quiz-state-text">
              No {activeQuizType === 'daily' ? 'Daily' : 'Milestone'} Quizzes Available
            </h4>
            <p className="quiz-state-text">
              {activeQuizType === 'daily' 
                ? 'Complete PrepLetters to unlock daily quizzes!' 
                : 'Reach milestone days to unlock milestone quizzes!'}
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Section Header */}
          <div className="quiz-card mb-4">
            <div className="quiz-card-header">
              <div>
                <h4 className="mb-2">
                  <i className={`bi ${activeQuizType === 'daily' ? 'bi-envelope-paper' : 'bi-trophy'} me-2`}></i>
                  {activeQuizType === 'daily' ? 'Daily Letter Quizzes' : 'Milestone Quizzes'}
                </h4>
                <p className="quiz-description-modern mt-0 mb-0">
                  {activeQuizType === 'daily' 
                    ? 'Test your knowledge from daily PrepLetters'
                    : 'Test your knowledge after completing milestone days'}
                </p>
              </div>
            </div>
          </div>

          {/* Today's Quiz Section (Daily Quizzes Only) */}
          {activeQuizType === 'daily' && getTodaysQuiz() && (
            <div className="quiz-card mb-4" style={{borderLeft: '4px solid #10b981'}}>
              <div className="quiz-card-header" style={{background: 'linear-gradient(90deg, rgba(16, 185, 129, 0.1) 0%, transparent 100%)'}}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-1">
                      <i className="bi bi-star-fill me-2 text-success"></i>Today's Quiz
                    </h5>
                    <p className="mb-0 quiz-description-modern" style={{fontSize: '0.9rem'}}>
                      Based on your latest PrepLetter #{userLastPrepLetterId}
                    </p>
                  </div>
                  <span className="badge-modern success">
                    Current
                  </span>
                </div>
              </div>
              <div className="quiz-card-body">
                {(() => {
                  const todaysQuiz = getTodaysQuiz();
                  return (
                    <div className="row align-items-center">
                      <div className="col-md-8">
                        <h5 className="quiz-item-title mb-2">{todaysQuiz.title}</h5>
                        <p className="quiz-description-modern mb-3">{todaysQuiz.description}</p>
                        {todaysQuiz.completed && (
                          <div className="mb-2">
                            <span className={`badge-modern ${getScoreBadgeClass(todaysQuiz.score)} me-2`}>
                              Score: {Math.round(todaysQuiz.score)}%
                            </span>
                            <span className="badge-modern success">
                              <i className="bi bi-check-circle"></i> Completed
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="col-md-4 text-end">
                        {todaysQuiz.completed ? (
                          <div className="d-flex flex-column gap-2">
                            <button
                              className="btn-modern btn-primary-modern btn-sm w-100"
                              onClick={() => handleStartQuiz(todaysQuiz)}
                              style={{fontSize: '0.9rem', padding: '8px 12px'}}
                            >
                              <i className="bi bi-arrow-repeat"></i> Retake Quiz
                            </button>
                            <button
                              className="btn-modern btn-secondary-modern btn-sm w-100"
                              onClick={() => navigate(`/quiz-history/${todaysQuiz.quiz_id}`)}
                              style={{fontSize: '0.9rem', padding: '8px 12px'}}
                            >
                              <i className="bi bi-bar-chart-line"></i> View Results
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn-modern btn-primary-modern w-100"
                            onClick={() => handleStartQuiz(todaysQuiz)}
                            style={{fontSize: '1.1rem', padding: '12px 24px'}}
                          >
                            <i className="bi bi-play-circle"></i> Start Today's Quiz
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Search Box (Daily Quizzes Only) */}
          {activeQuizType === 'daily' && (
            <div className="quiz-card mb-4">
              <div className="quiz-card-body">
                <div className="d-flex gap-2 align-items-center">
                  <div className="flex-grow-1 position-relative">
                    <input
                      type="number"
                      className="form-control search-input-modern"
                      placeholder="Search by Letter ID (e.g., 15, 16)..."
                      value={searchId}
                      onChange={handleSearchChange}
                      min="1"
                    />
                    <i className="bi bi-search search-icon-modern"></i>
                  </div>
                  {searchId && (
                    <button
                      className="btn-modern btn-secondary-modern"
                      onClick={handleClearSearch}
                      title="Clear search"
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Completion Status Tabs with Order Button */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="quiz-tabs">
              <button 
                className={`quiz-tab ${activeTab === 'all' ? 'active' : ''}`}
                onClick={() => handleFilterChange('all')}
              >
                All Quizzes
              </button>
              <button 
                className={`quiz-tab ${activeTab === 'completed' ? 'active' : ''}`}
                onClick={() => handleFilterChange('completed')}
              >
                Completed
              </button>
              <button 
                className={`quiz-tab ${activeTab === 'uncompleted' ? 'active' : ''}`}
                onClick={() => handleFilterChange('uncompleted')}
              >
                Not Completed
              </button>
            </div>
            {activeQuizType === 'daily' && (
              <button 
                className="btn-modern btn-secondary-modern btn-sm"
                onClick={handleSortOrderChange}
                style={{fontSize: '0.85rem', padding: '6px 12px'}}
              >
                <i className={`bi bi-sort-numeric-${sortOrder === 'asc' ? 'up' : 'down'}`}></i> 
                Order
              </button>
            )}
          </div>
          
          {/* Quiz Grid */}
          {(() => {
            const filteredQuizzes = getProcessedQuizzes();
            
            // Reset to first page if current page is out of bounds
            const totalPages = Math.ceil(filteredQuizzes.length / quizzesPerPage) || 1;
            if (currentPage > totalPages) {
              // Use setTimeout to avoid state updates during rendering
              setTimeout(() => setCurrentPage(1), 0);
            }
            
            // Calculate pagination
            const indexOfLastQuiz = currentPage * quizzesPerPage;
            const indexOfFirstQuiz = indexOfLastQuiz - quizzesPerPage;
            const currentQuizzes = filteredQuizzes.slice(indexOfFirstQuiz, indexOfLastQuiz);
            
            if (filteredQuizzes.length === 0) {
              return (
                <div className="quiz-card">
                  <div className="quiz-card-body text-center py-5">
                    <i className="bi bi-clipboard-x display-1 text-muted mb-3"></i>
                    <h4 className="quiz-state-text">No {filterOption !== 'all' ? filterOption : ''} Quizzes Found</h4>
                    <p className="quiz-state-text">
                      {filterOption === 'completed' ? 'You haven\'t completed any quizzes yet.' : 
                       filterOption === 'uncompleted' ? 'All quizzes have been completed!' : 
                       'No quizzes available at this time.'}
                    </p>
                  </div>
                </div>
              );
            }
            
            return (
              <>
                <div className="row">
                  {currentQuizzes.map((quiz) => (
                    <div key={quiz.quiz_id} className="col-md-6 col-lg-4 mb-4">
                      <div className={`quiz-card h-100 ${quiz.completed ? 'border-success-modern' : 'border-primary-modern'}`}>
                        <div className="quiz-card-header d-flex justify-content-between align-items-center">
                          <span className={`badge-modern ${activeQuizType === 'daily' ? 'info' : getMilestoneBadgeClass(quiz.milestone_day)}`}>
                            {activeQuizType === 'daily' ? `Letter #${quiz.letter_id}` : `Day ${quiz.milestone_day} Milestone`}
                          </span>
                          {quiz.completed && (
                            <span className="badge-modern success">
                              <i className="bi bi-check-circle"></i> Completed
                            </span>
                          )}
                        </div>
                        
                        <div className="quiz-card-body d-flex flex-column">
                          <h4 className="quiz-item-title">{quiz.title}</h4>
                          <p className="quiz-description-modern flex-grow-1">
                            {quiz.description}
                          </p>
                          
                          {quiz.completed && quiz.best_score !== null && (
                            <div className="quiz-details-completed mb-3">
                              <div className="quiz-best-score">
                                <div className="best-score-badge">
                                  <span className="trophy-icon">🏆</span>
                                  <span className="best-score-text">{Math.round(quiz.best_score)}%</span>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          <div className="mt-auto">
                            {quiz.completed ? (
                              <div className="d-flex gap-2">
                                <button
                                  className="btn-modern btn-primary-modern btn-sm w-50"
                                  onClick={() => handleStartQuiz(quiz)}
                                  style={{fontSize: '0.8rem', padding: '6px 8px'}}
                                >
                                  Retake
                                </button>
                                <button
                                  className="btn-modern btn-secondary-modern btn-sm w-50"
                                  onClick={() => navigate(`/quiz-history/${quiz.quiz_id}`)}
                                  style={{fontSize: '0.8rem', padding: '6px 8px'}}
                                >
                                  View Results
                                </button>
                              </div>
                            ) : (
                              <button
                                className="btn-modern btn-primary-modern w-100"
                                onClick={() => handleStartQuiz(quiz)}
                              >
                                <i className="bi bi-play-circle"></i> Start Quiz
                              </button>
                            )}
                          </div>
                        </div>
                        
                        <div className="quiz-card-footer text-muted">
                          <small className="quiz-description-modern">
                            <i className="bi bi-clock"></i> Time limit: {quiz.time_limit_minutes || (activeQuizType === 'daily' ? 5 : 10)} minutes
                          </small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center align-items-center mt-4 gap-2">
                    <button 
                      className="btn-modern btn-secondary-modern btn-sm" 
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      style={{fontSize: '0.85rem', padding: '6px 12px'}}
                    >
                      <i className="bi bi-chevron-left"></i> Previous
                    </button>
                    
                    <div className="pagination-modern">
                      {/* Show first page */}
                      {currentPage > 3 && (
                        <>
                          <button
                            className="btn-modern btn-secondary-modern btn-sm"
                            onClick={() => handlePageChange(1)}
                            style={{fontSize: '0.85rem', padding: '6px 12px', minWidth: '40px'}}
                          >
                            1
                          </button>
                          {currentPage > 4 && <span className="pagination-ellipsis">...</span>}
                        </>
                      )}
                      
                      {/* Show pages around current page */}
                      {Array.from({ length: Math.min(5, totalPages) }).map((_, index) => {
                        let pageNumber;
                        if (totalPages <= 5) {
                          pageNumber = index + 1;
                        } else if (currentPage <= 3) {
                          pageNumber = index + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNumber = totalPages - 4 + index;
                        } else {
                          pageNumber = currentPage - 2 + index;
                        }
                        
                        if (pageNumber < 1 || pageNumber > totalPages || 
                            (currentPage > 3 && pageNumber === 1) ||
                            (currentPage < totalPages - 2 && pageNumber === totalPages)) {
                          return null;
                        }
                        
                        return (
                          <button
                            key={pageNumber}
                            className={`btn-modern btn-sm ${currentPage === pageNumber ? 'btn-primary-modern' : 'btn-secondary-modern'}`}
                            onClick={() => handlePageChange(pageNumber)}
                            style={{fontSize: '0.85rem', padding: '6px 12px', minWidth: '40px'}}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}
                      
                      {/* Show last page */}
                      {currentPage < totalPages - 2 && totalPages > 5 && (
                        <>
                          {currentPage < totalPages - 3 && <span className="pagination-ellipsis">...</span>}
                          <button
                            className="btn-modern btn-secondary-modern btn-sm"
                            onClick={() => handlePageChange(totalPages)}
                            style={{fontSize: '0.85rem', padding: '6px 12px', minWidth: '40px'}}
                          >
                            {totalPages}
                          </button>
                        </>
                      )}
                    </div>
                    
                    <button 
                      className="btn-modern btn-secondary-modern btn-sm" 
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      style={{fontSize: '0.85rem', padding: '6px 12px'}}
                    >
                      Next <i className="bi bi-chevron-right"></i>
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </>
      )}

      {/* Quiz Tips */}
      <div className="quiz-card mt-4">
        <div className="quiz-card-header">
          <h5 className="mb-0">
            <i className="bi bi-lightbulb"></i> Quiz Tips
          </h5>
        </div>
        <div className="quiz-card-body">
          <div className="row">
            <div className="col-md-6">
              <ul className="list-unstyled">
                <li className="mb-2">
                  <i className="bi bi-check-circle text-success me-2"></i>
                  Read each question carefully
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle text-success me-2"></i>
                  Some questions allow multiple answers
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle text-success me-2"></i>
                  Review code snippets thoroughly
                </li>
              </ul>
            </div>
            <div className="col-md-6">
              <ul className="list-unstyled">
                <li className="mb-2">
                  <i className="bi bi-clock text-info me-2"></i>
                  Take your time - there's no rush
                </li>
                <li className="mb-2">
                  <i className="bi bi-arrow-left-right text-info me-2"></i>
                  Navigate between questions not allowed
                </li>
                <li className="mb-2">
                  <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                  Once started, quiz will be scored even if you exit early
                </li>
                <li className="mb-2">
                  <i className="bi bi-journal-text text-info me-2"></i>
                  Review your answers before submitting
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bug Report Link */}
      <div className="quiz-bug-report mt-4 text-center">
        <a 
          href="/bug-report?page=quiz-list&entity=dashboard" 
          className="bug-report-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          🐛 Report a Bug
        </a>
      </div>

    </div>
  );
};

export default QuizList;
